<template>
  <!-- 挂载容器：bind 模式下内嵌部分高度为 0，由 showCaptcha() 弹出浮层展示 -->
  <div ref="container" class="ali-captcha"></div>
</template>

<script>
/**
 * 阿里云图形验证码（Captcha4）封装组件
 *
 * 用法：
 *   <ali-captcha
 *     :captcha-id="captchaId"
 *     @success="onCaptchaSuccess"
 *     @error="onCaptchaError"
 *     ref="captcha"
 *   />
 *   this.$refs.captcha.show()   // 弹出图形验证码
 *
 * 验证成功后通过 @success 事件返回 getValidate() 结果：
 *   { lot_number, captcha_output, pass_token, gen_time }
 * 请将其提交给后端做二次校验（app_secret 签名逻辑在服务端）。
 *
 * ⚠️ 组件内不自动 reset：验证码会话数据在部分浏览器下会因立即 reset 被清空，
 *    且后端校验失败时需要重新验证。应由父组件在拿到结果、后端校验完成后，
 *    调用本组件的 reset() 方法。
 *
 * 说明：使用与官方 demo 一致的 product:'bind' + appendTo 容器写法，
 * showCaptcha() 负责弹出验证码浮层。show() 会等待 onNextReady/onReady
 * 资源就绪后才真正弹窗（未就绪时自动排队）。
 */
export default {
  name: 'AliCaptcha',
  props: {
    // 阿里云控制台申请的 captchaId（appId）
    captchaId: {
      type: String,
      required: true
    },
    // 语言：zh / en
    language: {
      type: String,
      default: 'zh'
    }
  },
  data() {
    return {
      captchaObj: null, // initAlicom4 回调返回的验证码实例，就绪前为 null
      ready: false, // 验证码资源就绪标记（onNextReady/onReady）
      pendingShow: false, // 未就绪时调用 show() 的记录，就绪后自动弹出
      hasShown: false // 是否已打开过验证码（第二次及以后打开前先 reset，确保全新题目）
    }
  },
  mounted() {
    if (!window.initAlicom4) {
      this.$emit('error', { msg: '图形验证码 SDK 加载失败' })
      return
    }
    const that = this
    window.initAlicom4(
      {
        captchaId: this.captchaId,
        product: 'bind', // 与官方 demo 一致的写法，showCaptcha 弹窗
        language: this.language
      },
      function (captchaObj) {
        that.captchaObj = captchaObj
        // 挂载到容器（demo 写法）
        if (typeof captchaObj.appendTo === 'function') {
          captchaObj.appendTo(that.$refs.container)
        }
        const readyCb = () => {
          that.ready = true
          // 若此前已调用过 show() 且资源未就绪，就绪后自动补一次弹窗
          if (that.pendingShow) {
            that.pendingShow = false
            that.hasShown = true
            that.captchaObj && that.captchaObj.showCaptcha()
          }
        }
        if (typeof captchaObj.onNextReady === 'function') {
          captchaObj.onNextReady(readyCb)
        } else if (typeof captchaObj.onReady === 'function') {
          captchaObj.onReady(readyCb)
        }
        if (typeof captchaObj.onSuccess === 'function') {
          captchaObj.onSuccess(function () {
            // 在 onSuccess 内同步取结果并交给父组件，随后由父组件显式 reset
            const result = captchaObj.getValidate && captchaObj.getValidate()
            that.$emit('success', result || {})
          })
        }
        if (typeof captchaObj.onError === 'function') {
          captchaObj.onError(function (e) {
            that.$emit('error', e)
          })
        }
        if (typeof captchaObj.onClose === 'function') {
          captchaObj.onClose(function () {
            that.$emit('close')
          })
        }
      }
    )
  },
  methods: {
    // 弹出图形验证码
    // - captchaObj 为空（初始化未完成）→ 返回 false 并触发 error
    // - 第二次及以后弹出 → 先 reset 再弹，保证是全新验证码
    //   （不依赖 onClose 是否触发，部分关闭方式不回调 onClose）
    // - 资源未就绪 → 排队，就绪后自动弹出，避免 showCaptcha 无响应
    show() {
      if (!this.captchaObj) {
        this.$emit('error', { msg: '验证码尚未就绪，请稍后重试' })
        return false
      }
      if (!this.ready) {
        this.pendingShow = true
        return true
      }
      // 第二次及以后打开：重置旧会话，避免弹出残留的旧验证码。
      // 验证结果在 onSuccess 里已同步取到并交给父组件，此时 reset 无数据风险。
      if (this.hasShown) {
        this.captchaObj.reset && this.captchaObj.reset()
      }
      this.hasShown = true
      this.captchaObj.showCaptcha()
      return true
    },
    // 重置验证码，供父组件在二次校验完成（成功或失败）后调用
    reset() {
      this.captchaObj &&
        this.captchaObj.reset &&
        this.captchaObj.reset()
    },
    destroy() {
      this.captchaObj &&
        this.captchaObj.destroy &&
        this.captchaObj.destroy()
    }
  },
  beforeDestroy() {
    this.destroy()
  }
}
</script>

<style scoped>
.ali-captcha {
  height: 0;
  overflow: hidden;
}
</style>
